import React from 'react';

/**
 * CuentaConectada — QUÉ CUENTA tiene conectada el cliente en una integración.
 *
 * POR QUÉ EXISTE (2026-07-31): la misma información se pintaba de TRES formas distintas
 * —verde con ✅ en el panel global, gris pequeño en los paneles de bot, y entre
 * paréntesis en `IntegrationsPanel`— y en varias integraciones no se pintaba en
 * absoluto aunque el dato estuviera guardado (Microsoft Calendar tenía `mailbox`,
 * Gmail/Outlook por bot tenían `accountName`). Un cliente con varias cuentas no podía
 * saber cuál había conectado.
 *
 * Se extrae en vez de replicar el estilo en cada fichero por lo mismo que
 * `creativeConstraints.js`: tres copias divergen, y este repo ya tiene un check de
 * paridad en el pre-commit porque eso ya pasó.
 *
 * ⚠️ DOS VARIANTES, y la razón NO es estética.
 * El patrón real de la app no es "verde con ✅": es **el estado de conexión destaca**.
 * Cómo se cumple depende de lo que cada panel ya tenga:
 *
 *   'global'  → verde + ✅. Es el ÚNICO indicador de conexión en `Integrations.jsx`,
 *               así que ahí carga con todo el peso. Es como estaba: no se toca.
 *
 *   'bot'     → etiqueta gris + email destacado. Los paneles de bot YA llevan encima un
 *               badge `.status-badge.connected` (fondo #c6f6d5) que dice "Conectado -
 *               Google Calendar". Añadir otro verde con otro ✅ daría dos verdes
 *               seguidos y tres señales de "está conectado" en tres líneas. Aquí el
 *               email destaca por CONTRASTE con el gris de alrededor, no por color.
 *
 * No reemplaza nada: el badge de estado, la fecha de conexión y el remitente siguen
 * donde estaban. La cuenta se AÑADE.
 */

const estilos = {
  global: {
    fila: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '0.5rem', marginBottom: '0.5rem',
      fontSize: '14px', fontWeight: '500', color: '#16a34a',
    },
    etiqueta: null,      // en global el texto va entero, traducido con la cuenta dentro
    cuenta: null,
  },
  bot: {
    fila: {
      display: 'flex', alignItems: 'baseline', gap: '0.375rem',
      margin: '0.5rem 0', fontSize: '0.875rem',
    },
    etiqueta: { color: '#718096' },                      // mismo gris que .connection-meta
    cuenta: { color: '#2d3748', fontWeight: 600 },       // destaca por contraste, no por color
  },
};

/**
 * @param {string}  cuenta    email o handle. Si es falsy, NO se renderiza nada:
 *                            inventar un "usuario@ejemplo" sería mentirle al cliente.
 * @param {'bot'|'global'} variante
 * @param {string}  etiqueta  texto ya traducido: "Conectado como" (bot) o la frase
 *                            completa con la cuenta interpolada (global).
 */
export default function CuentaConectada({ cuenta, variante = 'bot', etiqueta }) {
  // Sin cuenta no se muestra la línea. Las conexiones anteriores al scope `email` no
  // tienen el dato, y el panel ya muestra la fecha de conexión: mejor decir menos que
  // rellenar con un placeholder que el cliente leería como si fuera su cuenta.
  if (!cuenta) return null;

  const s = estilos[variante] || estilos.bot;

  if (variante === 'global') {
    return (
      <div style={s.fila}>
        <span style={{ fontSize: '16px' }}>✅</span>
        <span>{etiqueta}</span>
      </div>
    );
  }

  return (
    <p style={s.fila}>
      <span style={s.etiqueta}>{etiqueta}</span>
      <strong style={s.cuenta}>{cuenta}</strong>
    </p>
  );
}
